import { Popover } from "@headlessui/react";
import ArrowCircleBrokenRightIcon from "@svg/new-design/arrow-circle-broken-right";
import BagIcon from "@svg/new-design/bag-profile";
import Bookmark from "@svg/new-design/bookmark";
import LightOutlineProfile from "@svg/new-design/light-outline-profile";
import SubscriptionShineIcon from "@svg/new-design/SubscriptionShineIcon";
import Link from "next/link";
import { useRouter } from "next/router";
import { useUI } from "@context/ui-managed-context";
import auth from "@api/auth";
import { useAppContext } from "@context/app";
import { getCookie } from "@utils/Cookies";
import {
  COOKIE_ACCESS_TOKEN_KEY,
  COOKIE_FRONT_TOKEN_KEY,
  COOKIE_REFRESH_TOKEN_KEY,
} from "@utils/constants";
import { useSWRConfig } from "swr";
import { modalActions } from "@context/modals";
import { MODAL_TYPES } from "types/modals";
import { Fragment } from "react";
import { useLayoutContext } from "@context/layout";
import { twMerge } from "tailwind-merge";
import SubscriptionRemainingDays from "./subscription-remaining-days";
import MedalIcon from "@svg/new-design/medal";

const ProfileMenu = () => {
  const Cookie = getCookie();
  const router = useRouter();
  const { setUserLoggedIn, setLayoutLoaded } = useAppContext();
  const { cache, mutate } = useSWRConfig();
  const { setAlertView, openAlert } = useUI();
  const { subscriptionData, userData } = useLayoutContext();

  const logoutConfirmation = async () => {
    try {
      const res = await auth.logout().fetch();
      if (res.status) {
        Cookie.remove(COOKIE_ACCESS_TOKEN_KEY);
        Cookie.remove(COOKIE_REFRESH_TOKEN_KEY);
        Cookie.remove(COOKIE_FRONT_TOKEN_KEY);
        setUserLoggedIn(false);
        setLayoutLoaded(false);
      }
    } catch (error: any) {
      setAlertView("ALERT", [
        {
          mode: "danger",
          textBody: error.message || error.error.message || "دوباره تلاش کنید.",
        },
      ]);
      openAlert();
    } finally {
      modalActions.clearModals();

      setLayoutLoaded(false);
      if (cache instanceof Map) {
        const keys: string[] = Array.from(cache.keys());
        cache.clear();
        keys.map((key) => mutate(key));
      }

      router.push("/register");
    }
  };
  const logout = () => {
    modalActions.addModal(MODAL_TYPES.LOGOUT_CONFIRMATION, {
      config: {
        hideCloseIcon: true,
      },
      data: {
        logout: logoutConfirmation,
      },
    });
  };
  const isOrganizationUser = Boolean(userData?.user?.is_organization_user);
  const profileMenu = [
    {
      name: "علاقه‌مندی‌های من",
      icon: <Bookmark className="h-6 w-6" />,
      href: "/my-account/wishlist",
    },
    {
      name: "گواهینامه‌ها",
      icon: <MedalIcon className="h-6 w-6" />,
      href: "/my-account/certificates",
    },
    ...(isOrganizationUser
      ? [
          {
            name: "سازمانی",
            icon: <BagIcon className="h-6 w-6" />,
            href: "/my-account/organization",
          },
        ]
      : []),
    {
      name: "خروج از حساب کاربری",
      icon: <ArrowCircleBrokenRightIcon className="h-6 w-6" />,
      onclick: logout,
    },
  ];

  const hasActiveSubscription = Boolean(subscriptionData?.is_active);
  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            aria-label="منوی حساب کاربری"
            className={twMerge(
              "flex items-center justify-center rounded-6 default h-12 w-12 active:bg-fds-tertiary hover:bg-fds-secondary border border-transparent outline-none",
              hasActiveSubscription ? "bg-fds-brand-light rounded-full" : "",
              open
                ? hasActiveSubscription
                  ? "border-fds-brand-light"
                  : "bg-fds-secondary border-fds-select"
                : ""
            )}
          >
            {hasActiveSubscription ? (
              <div
                className="relative w-full h-full flex items-center justify-center"
                aria-hidden="true"
              >
                <div className="absolute top-0 -right-0.5 h-4 w-4">
                  <SubscriptionShineIcon
                    className="absolute inset-0 h-full w-full"
                    aria-hidden="true"
                  />
                </div>
                <LightOutlineProfile className="w-6 h-6" />
              </div>
            ) : (
              <LightOutlineProfile className="w-6 h-6" aria-hidden="true" />
            )}
          </Popover.Button>
          <Popover.Panel className="absolute z-10 flex flex-col p-3 overflow-hidden w-[264px] mt-4 left-0 rounded-16 bg-fds-primary shadow-primary-xlg">
            <SubscriptionRemainingDays closeHandler={close} />
            {profileMenu.map((profileItem) => (
              <Fragment key={profileItem.name}>
                {profileItem.onclick ? (
                  <button
                    onClick={() => {
                      profileItem.onclick();
                      close();
                    }}
                    className="my-1.5 p-2.5 w-full rounded-8 hover:bg-fds-brand-light items-center flex gap-x-2 text-fds-primary active:bg-fds-brand-light-hover"
                  >
                    {profileItem.icon}
                    <p className="text-fds-primary body-md">
                      {profileItem.name}
                    </p>
                  </button>
                ) : (
                  <Link
                    href={profileItem.href}
                    onClick={() => close()}
                    className="mt-1.5 p-2.5 w-full rounded-8 hover:bg-fds-brand-light items-center flex gap-x-2 text-fds-primary active:bg-fds-brand-light-hover"
                  >
                    {profileItem.icon}
                    <p className="text-fds-primary body-md">
                      {profileItem.name}
                    </p>
                  </Link>
                )}
              </Fragment>
            ))}
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
};
export default ProfileMenu;
